<script setup lang="ts">
import BaseIcon from "@/components/Base/Icon/Icon.vue";
import type { ButtonProps } from "@/types/components/Button.types";

defineOptions({ name: "BaseButton" });

const props = withDefaults(defineProps<ButtonProps>(), {
  size: "m",
  tertiaryColor: "main",
  iconPosition: "left",
  type: "button",
});

// `loading` implies `disabled` rather than being orthogonal to it. Beyond
// saving callers a second prop, it is what keeps the spinner honest: the
// legacy rule in `_utilities.scss` hides any `.spinner-border` inside a
// `button[type="submit"]` that is *not* `[disabled]`, so a loading-but-enabled
// submit button would spin invisibly while staying clickable — the exact
// double-submit this prop exists to prevent.
const isDisabled = computed(() => props.disabled || props.loading);

const componentType = computed(() => {
  // A disabled link must not navigate, so fall back to a native disabled
  // <button>. router-link / <a> have no `disabled` attribute and would stay
  // keyboard-navigable with only `pointer-events: none` as a guard.
  if (isDisabled.value) return "button";
  if (props.to) return "router-link";
  if (props.href) return "a";
  return "button";
});

const iconSize = computed(() => {
  switch (props.size) {
    case "s":
      return "md";
    case "l":
    case "xl":
      return "lg";
    default:
      return "body";
  }
});

const classes = computed(() => {
  const kindClass =
    props.kind === "tertiary"
      ? `base-btn--tertiary-${props.tertiaryColor}`
      : `base-btn--${props.kind}`;

  return [
    "base-btn",
    kindClass,
    `base-btn--size-${props.size}`,
    {
      "base-btn--icon-only": props.iconOnly,
      "base-btn--icon-only-mobile": props.iconOnlyMobile,
      "base-btn--icon-right": !props.iconOnly && props.iconPosition === "right",
      "base-btn--no-padding": props.noPadding,
      "base-btn--block": props.block && !props.iconOnly,
      "base-btn--disabled": isDisabled.value,
    },
  ];
});

const componentProps = computed(() => {
  const base: Record<string, unknown> = { class: classes.value };
  if (props.ariaLabel) base["aria-label"] = props.ariaLabel;

  if (props.loading) base["aria-busy"] = "true";

  // When disabled we always render a native <button> (see componentType), so
  // skip the to/href branches and emit the real disabled attribute.
  if (!isDisabled.value) {
    if (props.to) {
      return { ...base, to: props.to };
    }
    if (props.href) {
      return { ...base, href: props.href };
    }
  }
  return { ...base, type: props.type, disabled: isDisabled.value };
});

const hasIcon = computed(() => !!props.iconName || !!props.iconSvg);

if (import.meta.env.DEV) {
  if (props.iconOnly && !props.ariaLabel) {
    console.warn("[BaseButton] iconOnly requires ariaLabel for accessibility");
  }
  if (props.iconOnlyMobile && !props.ariaLabel) {
    console.warn(
      "[BaseButton] iconOnlyMobile requires ariaLabel for accessibility (text label is hidden on mobile)"
    );
  }
  if (props.iconName && props.iconSvg) {
    console.warn(
      "[BaseButton] iconName and iconSvg are mutually exclusive; iconName wins"
    );
  }
  if (props.to && props.href) {
    console.warn(
      "[BaseButton] `to` and `href` are mutually exclusive; `to` wins"
    );
  }
}
</script>

<template>
  <Component :is="componentType" v-bind="componentProps">
    <!-- Takes the icon's place so a button with an icon keeps its width for
         the duration. Deliberately not `Base/Spinner.vue`: its root is a
         <div>, and a <button> only takes phrasing content — the two hand-rolled
         spinners elsewhere nest a <div> in a button, which is fine as a one-off
         but not in the app's most-used button. `aria-hidden` because the busy
         state rides on the button's own `aria-busy`. -->
    <span
      v-if="loading"
      class="base-btn__spinner spinner-border spinner-border-sm"
      aria-hidden="true"
    />
    <BaseIcon
      v-else-if="hasIcon"
      :icon-name="iconName"
      :icon-svg="iconSvg"
      :size="iconSize"
      color="inherit"
    />
    <span
      v-if="!iconOnly"
      class="base-btn__label"
      :class="{ 'base-btn__label--hide-mobile': iconOnlyMobile }"
    >
      <slot></slot>
    </span>
  </Component>
</template>

<style scoped lang="scss">
.base-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: $spacer-2;
  border: none;
  border-radius: $border-radius-pill;
  font-family: inherit;
  font-weight: $font-weight-semibold;
  line-height: 1.5;
  white-space: nowrap;
  text-decoration: none;
  cursor: pointer;
  transition:
    background-color 0.15s ease-in-out,
    border-color 0.15s ease-in-out,
    color 0.15s ease-in-out;

  &__label {
    display: inline-flex;
    align-items: center;

    &--hide-mobile {
      @include media-breakpoint-down(md) {
        display: none;
      }
    }
  }

  // `_utilities.scss` carries a legacy Turbo rule that hides any
  // `.spinner-border` inside a `button[type="submit"]` unless the button is
  // `[disabled]`. `loading` already implies disabled, so the two agree — but
  // re-assert it here (0,2,0 beats the legacy 0,1,1) so this spinner never
  // depends on the button's `type`.
  &__spinner {
    display: inline-block;
    flex: none;
  }

  &--icon-right {
    flex-direction: row-reverse;
  }

  // --- Sizes (h, px, py, font-size) ---
  &--size-s {
    height: $base-btn-height-s;
    padding: $spacer-1 $spacer-3;
    font-size: $font-size-sm;
  }

  &--size-m {
    height: $base-btn-height-m;
    padding: $base-btn-padding-y-m $spacer-4;
    font-size: $font-size-base;
  }

  &--size-l {
    height: $base-btn-height-l;
    padding: $spacer-2 $spacer-4;
    font-size: $font-size-lg;
  }

  &--size-xl {
    height: $base-btn-height-xl;
    padding: $spacer-2 $spacer-5;
    font-size: $font-size-lg;
  }

  // --- Kind: primary ---
  &--primary {
    background-color: $color-primary;
    color: $color-surface-white;

    &:disabled,
    &.base-btn--disabled {
      background-color: $color-primary-light;
      color: $color-surface-white;
    }

    &:hover:not(.base-btn--disabled, :disabled) {
      background-color: $color-primary-dark;
      color: $color-surface-white;
    }
  }

  // --- Kind: secondary ---
  &--secondary {
    background-color: $color-secondary;
    color: $color-text;

    &:disabled,
    &.base-btn--disabled {
      background-color: $color-secondary-light;
      color: $color-surface-gray-500;
    }

    &:hover:not(.base-btn--disabled, :disabled) {
      background-color: $color-secondary-dark;
      color: $color-text;
    }
  }

  // --- Kind: outline (white secondary with a primary border) ---
  // The secondary CTA for grey page backgrounds, where the grey `secondary`
  // fill barely stands out (#13352, Figma 705-10857 / 799-11887). The 1px
  // border sits inside the fixed height, so it lines up with filled kinds.
  &--outline {
    border: 1px solid $color-primary;
    background-color: $color-surface-white;
    color: $color-primary;

    &:disabled,
    &.base-btn--disabled {
      border-color: $color-primary-light;
      background-color: $color-surface-white;
      color: $color-primary-light;
    }

    &:hover:not(.base-btn--disabled, :disabled) {
      border-color: $color-primary-dark;
      background-color: $color-secondary-light;
      color: $color-primary-dark;
    }
  }

  // --- Kind: upsell ---
  &--upsell {
    background-color: $color-upsell;
    color: $color-surface-white;

    &:disabled,
    &.base-btn--disabled {
      background-color: rgba($color-upsell, 0.4);
      color: $color-surface-white;
    }

    &:hover:not(.base-btn--disabled, :disabled) {
      background-color: $color-upsell-dark;
      color: $color-surface-white;
    }
  }

  // --- Kind: danger ---
  &--danger {
    background-color: $color-system-error;
    color: $color-surface-white;

    &:disabled,
    &.base-btn--disabled {
      background-color: $color-system-error-light;
      color: $color-surface-white;
    }

    &:hover:not(.base-btn--disabled, :disabled) {
      background-color: $color-system-error-dark;
      color: $color-surface-white;
    }
  }

  // --- Kind: tertiary (text-only, no background) ---
  // Heights match filled kinds so tertiary aligns vertically next to a
  // filled button (e.g. Cancel+Confirm in modal footers). Horizontal
  // padding is slightly reduced vs. filled kinds. Icon-only overrides reset it.
  &[class*="base-btn--tertiary-"] {
    background-color: transparent;

    &.base-btn--size-s {
      padding-left: $spacer-2;
      padding-right: $spacer-2;
    }

    &.base-btn--size-m {
      padding-left: $spacer-3;
      padding-right: $spacer-3;
    }

    &.base-btn--size-l {
      padding-left: $spacer-3;
      padding-right: $spacer-3;
    }

    &.base-btn--size-xl {
      padding-left: $spacer-4;
      padding-right: $spacer-4;
    }

    // The size compounds above out-rank the plain `.base-btn--no-padding`
    // rule, so restate it at matching specificity — otherwise the prop
    // silently keeps tertiary buttons padded (#11598).
    &.base-btn--no-padding {
      padding-left: 0;
      padding-right: 0;
    }
  }

  &--tertiary-main {
    color: $color-primary;

    &:disabled,
    &.base-btn--disabled {
      color: $color-primary-light;
    }

    &:hover:not(.base-btn--disabled, :disabled) {
      color: $color-primary-dark;
    }
  }

  &--tertiary-neutral {
    color: $color-text;

    &:disabled,
    &.base-btn--disabled {
      color: $color-text-light;
    }

    &:hover:not(.base-btn--disabled, :disabled) {
      color: $color-text-dark;
    }
  }

  &--tertiary-danger {
    color: $color-system-error;

    &:disabled,
    &.base-btn--disabled {
      color: $color-system-error-light;
    }

    &:hover:not(.base-btn--disabled, :disabled) {
      color: $color-system-error-dark;
    }
  }

  &--no-padding {
    padding: 0;

    &.base-btn--icon-only {
      width: auto;
    }
  }

  // Stretch to fill the parent's inline axis. Ignored for icon-only buttons,
  // which must stay square — guarded in the class binding.
  &--block {
    display: flex;
    width: 100%;
  }

  // --- Icon-only: square aspect + circle shape ---
  &--icon-only {
    gap: 0;

    &.base-btn--size-s {
      width: $base-btn-height-s;
      padding: 0;
    }

    &.base-btn--size-m {
      width: $base-btn-height-m;
      padding: 0;
    }

    &.base-btn--size-l {
      width: $base-btn-height-l;
      padding: 0;
    }

    &.base-btn--size-xl {
      width: $base-btn-height-xl;
      padding: 0;
    }
  }

  // --- Icon-only on mobile: keep label on desktop, collapse to a circle
  // below the md breakpoint (label hidden via base-btn__label--hide-mobile).
  &--icon-only-mobile {
    @include media-breakpoint-down(md) {
      gap: 0;
    }

    &.base-btn--size-s {
      @include media-breakpoint-down(md) {
        width: $base-btn-height-s;
        padding: 0;
      }
    }

    &.base-btn--size-m {
      @include media-breakpoint-down(md) {
        width: $base-btn-height-m;
        padding: 0;
      }
    }

    &.base-btn--size-l {
      @include media-breakpoint-down(md) {
        width: $base-btn-height-l;
        padding: 0;
      }
    }

    &.base-btn--size-xl {
      @include media-breakpoint-down(md) {
        width: $base-btn-height-xl;
        padding: 0;
      }
    }
  }

  // --- Disabled behavior (shared) ---
  &:disabled,
  &.base-btn--disabled {
    cursor: not-allowed;
    pointer-events: none;
  }
}
</style>
